import React from 'react';
import { Box, Snackbar, Alert, Fab, CircularProgress, Paper, Tooltip } from '@mui/material';
import UploadIcon from '@mui/icons-material/UploadFile';
import SendIcon from '@mui/icons-material/Send';
import VerifiedIcon from '@mui/icons-material/Verified';
import { useOutletContext } from 'react-router-dom';

import styles from './CreateConfig.styles';
import CreateConfigHeader from '../../components/CreateConfig/CreateConfigHeader';
import ConfigForm from '../../components/CreateConfig/ConfigForm';
import ConfigTable from '../../components/CreateConfig/ConfigTable';
import ConfigImport from '../../components/CreateConfig/ConfigImport';

import { useCreateConfigOptions } from '../../hooks/useCreateConfigOptions';
import { meterTypeOptions } from '../../utils/createConfig.helpers';
import buildRowFromForm from '../../utils/buildRowFromForm';
import validateConfigRow from '../../utils/validateConfigRow';

import useCreateConfigForm from '../../hooks/useCreateConfigForm';
import usePendingConfigRows from '../../hooks/usePendingConfigRows';
import useValidateConfigs from '../../hooks/useValidateConfigs';
import useProcessConfigs from '../../hooks/useProcessConfigs';
import useToast from '../../hooks/useToast';

export default function CreateConfig() {
  const { isSidebarExpanded } = useOutletContext();

  const { toast, setToast, close } = useToast();
  const { pending, setPending, add, addMany, updateAt, removeAt, clear } = usePendingConfigRows();

  const {
    operator, unit, evn, meterType, meterNumber, fromDate, comments,
    editingIdx, formErrors, formKey,
    setOperator, setUnit, setEvn, setMeterType, setMeterNumber, setFromDate, setComments,
    setFormErrors, setEditingIdx,
    resetForm, beginEdit,
  } = useCreateConfigForm({ meterTypeOptions });

  const {
    operatorOptions,
    unitOptions,
    evnOptions,
    meterNoOptions,
    nameToCode,
    codeToName,
    flags, 
  } = useCreateConfigOptions({ operator, unit, evn });

  const { validateAll } = useValidateConfigs({ setToast, setPending });
  const { processing, processValidated } = useProcessConfigs({ setToast });

  const handleAdd = () => {
    const row = buildRowFromForm({
      operator, unit, evn, meterType, meterNumber, fromDate, comments, nameToCode,
    });

    const issues = validateConfigRow(row);

    if (issues.length) {
      const errors = {};
      issues.forEach((msg) => {
        if (/operator/i.test(msg)) errors.operator = msg;
        else if (/unit/i.test(msg)) errors.unit = msg;
        else if (/evn/i.test(msg)) errors.evn = msg;
        else if (/meter number/i.test(msg)) errors.meterNumber = msg;
        else if (/meter type/i.test(msg)) errors.meterType = msg;
        else if (/fromdate|from date/i.test(msg)) errors.fromDate = msg;
        else if (/comments/i.test(msg)) errors.comments = msg;
      });
      setFormErrors(errors);
      return;
    }

    add(row);
    resetForm();
    setToast({ open: true, message: 'Configuration added.', severity: 'success' });
  };

  const saveEdit = () => {
    const row = buildRowFromForm({
      operator, unit, evn, meterType, meterNumber, fromDate, comments, nameToCode,
    });

    const issues = validateConfigRow(row);

    if (issues.length) {
      const errors = {};
      issues.forEach((msg) => {
        if (/operator/i.test(msg)) errors.operator = msg;
        else if (/unit/i.test(msg)) errors.unit = msg;
        else if (/evn/i.test(msg)) errors.evn = msg;
        else if (/meter number/i.test(msg)) errors.meterNumber = msg;
        else if (/meter type/i.test(msg)) errors.meterType = msg;
        else if (/fromdate|from date/i.test(msg)) errors.fromDate = msg;
        else if (/comments/i.test(msg)) errors.comments = msg;
      });
      setFormErrors(errors);
      return;
    }

    updateAt(editingIdx, row);
    resetForm();
    setEditingIdx(null);
  };

  const cancelEdit = () => {
    resetForm();
    setEditingIdx(null);
  };

  const handleValidateAll = () => validateAll(pending);

  const handleProcess = async () => {
    const rowsToSend = pending.filter((r) => r.Validated);
    const res = await processValidated(rowsToSend);
    if (res?.ok) clear();
  };

  const allValidated = pending.length > 0 && pending.every((r) => r.Validated);

  return (
    <>
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={close}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={toast.severity} sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>

      <Box
        sx={{
          ...styles.configDetails,
          marginLeft: isSidebarExpanded ? '240px' : '95px',
          width: isSidebarExpanded ? 'calc(100vw - 240px)' : 'calc(100vw - 95px)',
          transition: 'all 0.3s ease',
        }}
      >
        <Box sx={styles.headerRow}>
          <Box sx={styles.headerBar}>
            <CreateConfigHeader title="Create Configurations" />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Tooltip title="Import configurations" arrow>
                <span>
                  <Fab
                    data-testid="import-btn"
                    size="small"
                    sx={styles.fabButton}
                    onClick={() => document.getElementById('file-import').click()}
                    disabled={editingIdx !== null}
                    aria-label="Import"
                  >
                    <UploadIcon fontSize="small" />
                  </Fab>
                </span>
              </Tooltip>

              <Tooltip title="Validate all" arrow>
                <span>
                  <Fab
                    data-testid="validate-btn"
                    size="small"
                    sx={styles.fabButton}
                    onClick={handleValidateAll}
                    disabled={!pending.length || editingIdx !== null}
                    aria-label="Validate"
                  >
                    <VerifiedIcon fontSize="small" />
                  </Fab>
                </span>
              </Tooltip>

              <Tooltip title={processing ? 'Processing…' : 'Process validated rows'} arrow>
                <span>
                  <Fab
                    data-testid="process-btn"
                    size="small"
                    sx={styles.fabButton}
                    onClick={handleProcess}
                    disabled={!allValidated || processing || editingIdx !== null}
                    aria-label="Process"
                  >
                    {processing ? (
                      <CircularProgress data-testid="process-loader" size={18} sx={{ color: 'inherit' }} />
                    ) : (
                      <SendIcon fontSize="small" />
                    )}
                  </Fab>
                </span>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <Paper sx={styles.formCard}>
          <ConfigForm
            formKey={formKey}
            operatorOptions={operatorOptions}
            unitOptions={unitOptions}
            evnOptions={evnOptions}
            meterTypeOptions={meterTypeOptions}
            meterNoOptions={meterNoOptions}
            operator={operator}
            unit={unit}
            evn={evn}
            meterType={meterType}
            meterNumber={meterNumber}
            fromDate={fromDate}
            comments={comments}
            formErrors={formErrors}
            setOperator={setOperator}
            setUnit={setUnit}
            setEvn={setEvn}
            setMeterType={setMeterType}
            setMeterNumber={setMeterNumber}
            setFromDate={setFromDate}
            setComments={setComments}
            handleAdd={handleAdd}
            editing={editingIdx !== null}
            handleClear={resetForm}
            onSave={saveEdit}
            onCancel={cancelEdit}
            hasOperator={flags.hasOperator}
            isExistingUnit={flags.isExistingUnit}
            isExistingEvn={flags.isExistingEvn}
          />
        </Paper>

        <Paper sx={styles.tableCard}>
          <ConfigImport
            onImport={(r) => {
              const { success, message, rows } = r;
              setToast({ open: true, message, severity: success ? 'success' : 'error' });
              if (success) addMany(rows);
            }}
          />

          <ConfigTable
            rows={pending}
            onRemove={removeAt}
            onStartEdit={(idx) => beginEdit(pending[idx], idx)}
            editingIdx={editingIdx}
            codeToName={codeToName}
          />
        </Paper>
      </Box>
    </>
  );
}
